import { workoutRules, type WorkoutIntensity } from "../../backend/src/rules/activity";

export type ActivityVoiceFields = {
  type?: string;
  intensity?: WorkoutIntensity;
  durationMinutes?: number;
  distanceKm?: number;
  restMinutes?: number;
  steps?: number;
  calories?: number;
};

const words: Record<string, string> = {
  ноль: "0", один: "1", одна: "1", одно: "1", два: "2", две: "2", три: "3", четыре: "4", пять: "5", шесть: "6", семь: "7", восемь: "8", девять: "9", десять: "10", пятнадцать: "15", двадцать: "20", тридцать: "30", сорок: "40", пятьдесят: "50", шестьдесят: "60", полчаса: "30",
};
function normalize(text: string): string {
  return text.toLocaleLowerCase("ru-RU").replace(/ё/g, "е").replace(/(?<!\p{L})(ноль|один|одна|одно|два|две|три|четыре|пять|шесть|семь|восемь|девять|десять|пятнадцать|двадцать|тридцать|сорок|пятьдесят|шестьдесят|полчаса)(?!\p{L})/gu, word => words[word] ?? word);
}
function amount(text: string, pattern: RegExp): number | undefined {
  const found = text.match(pattern);
  const value = found?.[1] ? Number(found[1].replace(",", ".")) : NaN;
  return Number.isFinite(value) && value > 0 ? value : undefined;
}
export function parseActivityVoice(text: string): ActivityVoiceFields {
  const phrase = normalize(text);
  const result: ActivityVoiceFields = {};
  const selected = workoutRules.find(rule => phrase.includes(rule.name.toLocaleLowerCase("ru-RU")))
    ?? workoutRules.find(rule => rule.id === "swimming" && /(плавал|плавала|проплыл|проплыла|плавание)/u.test(phrase))
    ?? workoutRules.find(rule => rule.id === "walking" && /(ходил|ходила|прошел|прошла|ходьба)/u.test(phrase))
    ?? workoutRules.find(rule => rule.id === "running" && /(бегал|бегала|пробежал|пробежала|бег)/u.test(phrase));
  if (selected) result.type = selected.name;
  if (/(легк|спокойн|медленн)/u.test(phrase)) result.intensity = "light";
  else if (/(интенсивн|тяжел|быстр|сильн)/u.test(phrase)) result.intensity = "vigorous";
  else if (/(обычн|средн|умеренн)/u.test(phrase)) result.intensity = "moderate";
  const rest = amount(phrase, /(?:отдых[а-я]*|пауз[а-я]*|перерыв[а-я]*)\s*(\d+(?:[.,]\d+)?)\s*(?:минут[а-я]*|мин)?/u)
    ?? amount(phrase, /(\d+(?:[.,]\d+)?)\s*(?:минут[а-я]*|мин)\s*(?:отдых[а-я]*|пауз[а-я]*|перерыв[а-я]*)/u);
  const movementText = phrase.replace(/(?:отдых[а-я]*|пауз[а-я]*|перерыв[а-я]*)\s*\d+(?:[.,]\d+)?\s*(?:минут[а-я]*|мин)?/gu, "").replace(/\d+(?:[.,]\d+)?\s*(?:минут[а-я]*|мин)\s*(?:отдых[а-я]*|пауз[а-я]*|перерыв[а-я]*)/gu, "");
  const hours = amount(movementText, /(\d+(?:[.,]\d+)?)\s*(?:час\w*|ч\b)/u);
  const minutes = amount(movementText, /(\d+(?:[.,]\d+)?)\s*(?:минут\w*|мин\b)/u);
  const implicitHour = !hours && /(?:^|\s)(?:за\s+)?час(?:\s|$|,)/u.test(movementText) ? 1 : 0;
  if (hours || minutes || implicitHour) result.durationMinutes = ((hours ?? implicitHour) * 60) + (minutes ?? 0);
  if (rest) result.restMinutes = rest;
  const distanceKm = amount(phrase, /(\d+(?:[.,]\d+)?)\s*(?:километр\w*|км)/u);
  if (distanceKm) result.distanceKm = distanceKm;
  if (!result.distanceKm) {
    const meters = amount(phrase, /(\d+(?:[.,]\d+)?)\s*(?:метр\w*|м\b)/u);
    if (meters) result.distanceKm = meters / 1000;
  }
  const thousands = amount(phrase, /(\d+(?:[.,]\d+)?)\s*(?:тысяч\w*)\s*(?:шаг\w*)/u);
  const steps = thousands ? thousands * 1000 : amount(phrase, /(\d+(?:[.,]\d+)?)\s*(?:шаг\w*)/u);
  if (steps) result.steps = steps;
  const calories = amount(phrase, /(\d+(?:[.,]\d+)?)\s*(?:ккал|калори\w*)/u);
  if (calories) result.calories = calories;
  return result;
}
